
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Users, UserPlus, Heart, MessageCircle, UserX } from 'lucide-react';
import { SecureProfilePhoto } from './SecureProfilePhoto';
import { userConnectionsService, UserConnection } from '@/services/userConnectionsService';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { supabaseUserService, SupabaseUser } from '@/services/supabaseUserService';
import { internalUserService, InternalUser } from '@/services/internalUserService';
import { teamService } from '@/services/teamService';
import { supabase } from '@/integrations/supabase/client';

interface ConnectionsManagerProps {
  userId: string;
  userType: 'supabase' | 'internal';
}

interface EnhancedUserConnection extends UserConnection {
  user_details?: {
    username?: string;
    first_name?: string;
    last_name?: string;
    role?: string;
    profile_photo_url?: string;
  };
}

type ResolvedUserDetails = NonNullable<EnhancedUserConnection['user_details']>;

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const normalizeConnectionIdentifier = (identifier: string) => identifier.trim().replace(/^@/, '');

const buildResolvedUserDetails = (
  account: { username?: string | null; role?: string | null } | null,
  profile: { first_name?: string | null; last_name?: string | null; profile_photo_url?: string | null } | null
): ResolvedUserDetails | null => {
  if (!account && !profile) return null;

  return {
    username: account?.username ?? undefined,
    role: account?.role ?? undefined,
    first_name: profile?.first_name ?? undefined,
    last_name: profile?.last_name ?? undefined,
    profile_photo_url: profile?.profile_photo_url ?? undefined,
  };
};

const resolveInternalUserDetails = async (identifier: string): Promise<ResolvedUserDetails | null> => {
  const normalizedIdentifier = normalizeConnectionIdentifier(identifier);
  if (!normalizedIdentifier) return null;

  if (UUID_PATTERN.test(normalizedIdentifier)) {
    const [{ data: internalUser }, { data: profile }] = await Promise.all([
      supabase
        .from('internal_users')
        .select('username, role')
        .eq('id', normalizedIdentifier)
        .maybeSingle(),
      supabase
        .from('internal_user_profiles')
        .select('first_name, last_name, profile_photo_url')
        .eq('user_id', normalizedIdentifier)
        .maybeSingle(),
    ]);

    const details = buildResolvedUserDetails(internalUser, profile);
    if (details) return details;
  }

  const { data: matchedUser } = await supabase
    .from('internal_users')
    .select('id, username, role')
    .ilike('username', normalizedIdentifier)
    .limit(1)
    .maybeSingle();

  if (matchedUser) {
    const { data: profile } = await supabase
      .from('internal_user_profiles')
      .select('first_name, last_name, profile_photo_url')
      .eq('user_id', matchedUser.id)
      .maybeSingle();

    return buildResolvedUserDetails(matchedUser, profile);
  }

  const nameParts = normalizedIdentifier.split(/\s+/).filter(Boolean);

  if (nameParts.length >= 2) {
    const firstName = nameParts[0];
    const lastName = nameParts.slice(1).join(' ');

    const { data: matchedProfile } = await supabase
      .from('internal_user_profiles')
      .select('user_id, first_name, last_name, profile_photo_url')
      .ilike('first_name', firstName)
      .ilike('last_name', lastName)
      .limit(1)
      .maybeSingle();

    if (matchedProfile) {
      const { data: account } = await supabase
        .from('internal_users')
        .select('username, role')
        .eq('id', matchedProfile.user_id)
        .maybeSingle();

      return buildResolvedUserDetails(account, matchedProfile);
    }
  }

  return null;
};

const resolveSupabaseUserDetails = async (identifier: string): Promise<ResolvedUserDetails | null> => {
  const normalizedIdentifier = normalizeConnectionIdentifier(identifier);
  if (!normalizedIdentifier) return null;

  if (UUID_PATTERN.test(normalizedIdentifier)) {
    const { data } = await supabase
      .from('profiles')
      .select('username, first_name, last_name, role, profile_photo_url')
      .eq('id', normalizedIdentifier)
      .maybeSingle();

    if (data) {
      return buildResolvedUserDetails(
        { username: data.username, role: data.role },
        {
          first_name: data.first_name,
          last_name: data.last_name,
          profile_photo_url: data.profile_photo_url,
        }
      );
    }
  }

  const { data: matchedProfile } = await supabase
    .from('profiles')
    .select('username, first_name, last_name, role, profile_photo_url')
    .ilike('username', normalizedIdentifier)
    .limit(1)
    .maybeSingle();

  if (matchedProfile) {
    return buildResolvedUserDetails(
      { username: matchedProfile.username, role: matchedProfile.role },
      {
        first_name: matchedProfile.first_name,
        last_name: matchedProfile.last_name,
        profile_photo_url: matchedProfile.profile_photo_url,
      }
    );
  }

  const nameParts = normalizedIdentifier.split(/\s+/).filter(Boolean);

  if (nameParts.length >= 2) {
    const firstName = nameParts[0];
    const lastName = nameParts.slice(1).join(' ');

    const { data: profileByName } = await supabase
      .from('profiles')
      .select('username, first_name, last_name, role, profile_photo_url')
      .ilike('first_name', firstName)
      .ilike('last_name', lastName)
      .limit(1)
      .maybeSingle();

    if (profileByName) {
      return buildResolvedUserDetails(
        { username: profileByName.username, role: profileByName.role },
        {
          first_name: profileByName.first_name,
          last_name: profileByName.last_name,
          profile_photo_url: profileByName.profile_photo_url,
        }
      );
    }
  }

  return null;
};

const resolveConnectionUserDetails = async (
  identifier: string,
  preferredType: 'supabase' | 'internal'
): Promise<ResolvedUserDetails | null> => {
  const resolvers = preferredType === 'internal'
    ? [resolveInternalUserDetails, resolveSupabaseUserDetails]
    : [resolveSupabaseUserDetails, resolveInternalUserDetails];

  for (const resolver of resolvers) {
    const details = await resolver(identifier);
    if (details) return details;
  }

  return null;
};

export const ConnectionsManager: React.FC<ConnectionsManagerProps> = ({ userId, userType }) => {
  const [connections, setConnections] = useState<EnhancedUserConnection[]>([]);
  const [followers, setFollowers] = useState<EnhancedUserConnection[]>([]);
  const [pendingRequests, setPendingRequests] = useState<EnhancedUserConnection[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    loadConnections();
  }, [userId]);

  const loadConnections = async () => {
    try {
      setLoading(true);
      const userDetailsCache = new Map<string, Promise<ResolvedUserDetails | null>>();

      const getResolvedUserDetails = (identifier: string, preferredType: 'supabase' | 'internal') => {
        const cacheKey = `${preferredType}:${normalizeConnectionIdentifier(identifier).toLowerCase()}`;

        if (!userDetailsCache.has(cacheKey)) {
          userDetailsCache.set(cacheKey, resolveConnectionUserDetails(identifier, preferredType));
        }

        return userDetailsCache.get(cacheKey)!;
      };
      
      // Load outgoing connections from user_connections table
      const outgoingConnections = await userConnectionsService.getConnections(userId);
      // Type guard to ensure proper typing
      const typedConnections = (outgoingConnections || []).map(conn => ({
        ...conn,
        connection_type: conn.connection_type as 'follow' | 'connect' | 'block',
        user_type: conn.user_type as 'supabase' | 'internal',
        connected_user_type: conn.connected_user_type as 'supabase' | 'internal',
        status: conn.status as 'pending' | 'accepted' | 'rejected'
      }));

      // Fetch user details for each connection
      const connectionsWithDetails = await Promise.all(
        typedConnections.map(async (conn) => {
          let userDetails = null;

          try {
            userDetails = await getResolvedUserDetails(conn.connected_user_id, conn.connected_user_type);
          } catch (error) {
            console.error('Error fetching connection user details:', error);
          }
          
          return {
            ...conn,
            user_details: userDetails
          };
        })
      );

      // Also load coach profile connections if user is internal
      if (userType === 'internal') {
        try {
          const { supabase } = await import('@/integrations/supabase/client');
          const { data: profileData } = await supabase
            .from('internal_user_profiles')
            .select('connected_players, connected_coaches, role')
            .eq('user_id', userId)
            .single();

          if (profileData?.role === 'coach') {
            // Convert profile connections to display format
            const profileConnections: UserConnection[] = [];

            // Add connected players
            if (Array.isArray(profileData.connected_players)) {
              for (const playerId of profileData.connected_players) {
                if (playerId && typeof playerId === 'string') {
                  profileConnections.push({
                    id: `profile-player-${playerId}`,
                    user_id: userId,
                    connected_user_id: playerId,
                    connection_type: 'connect',
                    user_type: 'internal',
                    connected_user_type: 'internal',
                    status: 'accepted',
                    created_at: new Date().toISOString(),
                    updated_at: new Date().toISOString()
                  });
                }
              }
            }

            // Add connected coaches
            if (Array.isArray(profileData.connected_coaches)) {
              for (const coachId of profileData.connected_coaches) {
                if (coachId && typeof coachId === 'string') {
                  profileConnections.push({
                    id: `profile-coach-${coachId}`,
                    user_id: userId,
                    connected_user_id: coachId,
                    connection_type: 'connect',
                    user_type: 'internal',
                    connected_user_type: 'internal',
                    status: 'accepted',
                    created_at: new Date().toISOString(),
                    updated_at: new Date().toISOString()
                  });
                }
              }
            }

            // Merge with existing connections (avoid duplicates)
            const existingIds = new Set(connectionsWithDetails.map(c => c.connected_user_id));
            const newConnections = profileConnections.filter(pc => !existingIds.has(pc.connected_user_id));
            // Fetch user details for profile connections
            const newConnectionsWithDetails = await Promise.all(
              newConnections.map(async (nc) => {
                let userDetails = null;

                try {
                  userDetails = await getResolvedUserDetails(nc.connected_user_id, nc.connected_user_type);
                } catch (error) {
                  console.error('Error fetching profile connection details:', error);
                }
                return { ...nc, user_details: userDetails };
              })
            );
            connectionsWithDetails.push(...newConnectionsWithDetails);
          }
        } catch (profileError) {
          console.error('Error loading profile connections:', profileError);
        }
      }
      
      setConnections(connectionsWithDetails);

      // Filter pending requests
      const pending = connectionsWithDetails.filter(conn => conn.status === 'pending');
      setPendingRequests(pending);

    } catch (error) {
      console.error('Error loading connections:', error);
      toast({
        title: "Error",
        description: "Failed to load connections",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptRequest = async (connectionId: string, connectedUserId: string) => {
    try {
      await userConnectionsService.updateConnectionStatus(
        userId,
        connectedUserId,
        'connect',
        'accepted'
      );
      
      toast({
        title: "Request Accepted",
        description: "Connection request accepted successfully",
      });
      
      loadConnections();
    } catch (error) {
      console.error('Error accepting request:', error);
      toast({
        title: "Error",
        description: "Failed to accept request",
        variant: "destructive"
      });
    }
  };

  const handleRejectRequest = async (connectionId: string, connectedUserId: string) => {
    try {
      await userConnectionsService.updateConnectionStatus(
        userId,
        connectedUserId,
        'connect',
        'rejected'
      );
      
      toast({
        title: "Request Rejected",
        description: "Connection request rejected",
      });
      
      loadConnections();
    } catch (error) {
      console.error('Error rejecting request:', error);
      toast({
        title: "Error",
        description: "Failed to reject request",
        variant: "destructive"
      });
    }
  };

  const handleRemoveConnection = async (connectedUserId: string, connectionType: 'follow' | 'connect') => {
    try {
      await userConnectionsService.removeConnection(userId, connectedUserId, connectionType);
      
      toast({
        title: "Connection Removed",
        description: "Connection removed successfully",
      });
      
      loadConnections();
    } catch (error) {
      console.error('Error removing connection:', error);
      toast({
        title: "Error",
        description: "Failed to remove connection",
        variant: "destructive"
      });
    }
  };

  const handleMessage = async (connectedUserId: string, connectionDetails?: ResolvedUserDetails) => {
    try {
      // Try to find an existing DM group between these two users
      const { data: existingGroups } = await supabase
        .from('chat_group_users')
        .select('chat_group_id')
        .eq('user_id', userId);

      if (existingGroups && existingGroups.length > 0) {
        // Check which of these groups also contain the connected user
        const groupIds = existingGroups.map(g => g.chat_group_id);
        const { data: sharedGroups } = await supabase
          .from('chat_group_users')
          .select('chat_group_id')
          .eq('user_id', connectedUserId)
          .in('chat_group_id', groupIds);

        if (sharedGroups && sharedGroups.length > 0) {
          // Check if any shared group is a DM (only 2 members)
          for (const sg of sharedGroups) {
            const { count } = await supabase
              .from('chat_group_users')
              .select('*', { count: 'exact', head: true })
              .eq('chat_group_id', sg.chat_group_id);

            if (count === 2) {
              // Found existing DM, navigate to it
              navigate(`/chat/${sg.chat_group_id}`);
              return;
            }
          }
        }
      }

      // No existing DM found — create one
      const connectedUsername = connectionDetails?.username || 
        `${connectionDetails?.first_name || ''} ${connectionDetails?.last_name || ''}`.trim() || 
        'User';

      // Get current user's username
      let currentUsername = 'Me';
      try {
        const { data: currentProfile } = await supabase
          .from('internal_user_profiles')
          .select('first_name, last_name')
          .eq('user_id', userId)
          .maybeSingle();
        if (currentProfile) {
          currentUsername = `${currentProfile.first_name || ''} ${currentProfile.last_name || ''}`.trim() || 'Me';
        }
        if (currentUsername === 'Me') {
          const { data: supProfile } = await supabase
            .from('profiles')
            .select('username')
            .eq('id', userId)
            .maybeSingle();
          if (supProfile?.username) currentUsername = supProfile.username;
        }
      } catch {}

      const chatName = `${currentUsername} & ${connectedUsername}`;

      const { data: newGroup, error: createError } = await supabase
        .from('chat_groups')
        .insert({
          chat_name: chatName,
          created_by: userId,
          is_active: true
        })
        .select()
        .single();

      if (createError || !newGroup) {
        throw new Error('Failed to create chat group');
      }

      // Add both users to the group
      await supabase.from('chat_group_users').insert([
        { chat_group_id: newGroup.chat_id, user_id: userId },
        { chat_group_id: newGroup.chat_id, user_id: connectedUserId }
      ]);

      navigate(`/chat/${newGroup.chat_id}`);
    } catch (error) {
      console.error('Error creating DM:', error);
      toast({
        title: "Error",
        description: "Failed to open message. Please try again.",
        variant: "destructive"
      });
    }
  };

  const getInitials = (user: any) => {
    if (!user) return 'U';
    
    const firstName = user.first_name?.[0] || '';
    const lastName = user.last_name?.[0] || '';
    const initials = (firstName + lastName).toUpperCase();
    
    if (initials) return initials;
    if (user.username?.[0]) return user.username[0].toUpperCase();
    
    return 'U';
  };

  if (loading) {
    return (
      <Card className="bg-[#0d1117] border border-gray-800">
        <CardContent className="pt-6">
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-sports-green"></div>
            <p className="mt-2 text-gray-400">Loading connections...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-[#0d1117] border border-gray-800">
      <CardHeader>
        <CardTitle className="text-sports-green flex items-center gap-2">
          <Users className="h-5 w-5" />
          My Connections
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="connections" className="w-full">
          <TabsList className="grid w-full grid-cols-3 bg-gray-800">
            <TabsTrigger value="connections">Connections</TabsTrigger>
            <TabsTrigger value="following">Following</TabsTrigger>
            <TabsTrigger value="pending">Pending</TabsTrigger>
          </TabsList>
          
          <TabsContent value="connections" className="space-y-4">
            {connections.filter(conn => conn.connection_type === 'connect' && conn.status === 'accepted').length === 0 ? (
              <div className="text-center py-8">
                <Users className="h-12 w-12 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400">No connections yet</p>
                <Button 
                  onClick={() => navigate('/user-discovery')}
                  className="mt-4 bg-sports-green text-black hover:bg-green-600"
                >
                  Discover Users
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {connections
                  .filter(conn => conn.connection_type === 'connect' && conn.status === 'accepted')
                  .map((connection) => (
                    <div key={connection.id} className="flex items-center justify-between p-4 bg-gray-800/50 rounded-lg">
                       <div className="flex items-center gap-3">
                          <SecureProfilePhoto
                            filePath={connection.user_details?.profile_photo_url || undefined}
                            fallbackInitials={getInitials(connection.user_details)}
                            size="sm"
                          />
                        <div>
                          <p className="font-medium text-white">
                            {connection.user_details?.username || 
                             `${connection.user_details?.first_name || ''} ${connection.user_details?.last_name || ''}`.trim() ||
                             'Unknown User'}
                          </p>
                          <p className="text-sm text-gray-400">
                            {connection.user_details?.role ? 
                              `${connection.user_details.role.charAt(0).toUpperCase() + connection.user_details.role.slice(1)} (${connection.connected_user_type})` :
                              connection.id.startsWith('profile-player-') ? 'Connected Player' :
                              connection.id.startsWith('profile-coach-') ? 'Connected Coach' :
                              connection.id.startsWith('profile-team-') ? 'Connected Team' :
                              'Connection'
                            }
                          </p>
                        </div>
                       </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => handleMessage(connection.connected_user_id, connection.user_details)}
                          className="bg-purple-600 hover:bg-purple-700"
                        >
                          <MessageCircle className="h-3 w-3" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleRemoveConnection(connection.connected_user_id, 'connect')}
                          className="border-red-600 text-red-600 hover:bg-red-600 hover:text-white"
                        >
                          <UserX className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </TabsContent>
          
          <TabsContent value="following" className="space-y-4">
            {connections.filter(conn => conn.connection_type === 'follow').length === 0 ? (
              <div className="text-center py-8">
                <Heart className="h-12 w-12 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400">Not following anyone yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {connections
                  .filter(conn => conn.connection_type === 'follow')
                  .map((connection) => (
                    <div key={connection.id} className="flex items-center justify-between p-4 bg-gray-800/50 rounded-lg">
                       <div className="flex items-center gap-3">
                         <SecureProfilePhoto
                           filePath={connection.user_details?.profile_photo_url}
                           fallbackInitials={getInitials(connection.user_details)}
                           size="sm"
                         />
                         <div>
                           <p className="font-medium text-white">
                             {connection.user_details?.username || 
                              `${connection.user_details?.first_name || ''} ${connection.user_details?.last_name || ''}`.trim() ||
                              'Unknown User'}
                           </p>
                           <p className="text-sm text-gray-400">
                             {connection.user_details?.role ? 
                               `Following ${connection.user_details.role.charAt(0).toUpperCase() + connection.user_details.role.slice(1)}` :
                               'Following'
                             }
                           </p>
                         </div>
                       </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleRemoveConnection(connection.connected_user_id, 'follow')}
                        className="border-gray-600 text-gray-400 hover:bg-gray-600 hover:text-white"
                      >
                        Unfollow
                      </Button>
                    </div>
                  ))}
              </div>
            )}
          </TabsContent>
          
          <TabsContent value="pending" className="space-y-4">
            {pendingRequests.length === 0 ? (
              <div className="text-center py-8">
                <UserPlus className="h-12 w-12 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400">No pending requests</p>
              </div>
            ) : (
              <div className="space-y-4">
                {pendingRequests.map((request) => (
                  <div key={request.id} className="flex items-center justify-between p-4 bg-gray-800/50 rounded-lg">
                     <div className="flex items-center gap-3">
                       <SecureProfilePhoto
                         filePath={request.user_details?.profile_photo_url}
                         fallbackInitials={getInitials(request.user_details)}
                         size="sm"
                       />
                       <div>
                         <p className="font-medium text-white">
                           {request.user_details?.username || 
                            `${request.user_details?.first_name || ''} ${request.user_details?.last_name || ''}`.trim() ||
                            'Unknown User'}
                         </p>
                         <p className="text-sm text-gray-400">
                           {request.user_details?.role ? 
                             `Pending ${request.user_details.role.charAt(0).toUpperCase() + request.user_details.role.slice(1)} Request` :
                             'Connection request sent'
                           }
                         </p>
                       </div>
                     </div>
                    <Badge variant="outline" className="text-yellow-400 border-yellow-400">
                      Pending
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default ConnectionsManager;
